import { query } from "./strapi";

// Define interfaces for our service
interface ZoneCombination {
  originLeadTimeZoneId: string;
  originFeeZoneId: string;
  destinationLeadTimeZoneId: string;
  destinationFeeZoneId: string;
}

interface OpsLeadTimeData {
  opsLeadTime: number;
  originZone: string | null;
  destinationZone: string | null;
}

interface DeliveryMethodsData {
  [key: string]: boolean;
}

export interface UnifiedDeliveryData {
  originLeadTimeZoneId: string;
  originFeeZoneId: string;
  destinationLeadTimeZoneId: string;
  destinationFeeZoneId: string;
  opsLeadTime: OpsLeadTimeData;
  deliveryMethods: DeliveryMethodsData;
}

// Cache implementation
const deliveryDataCache = new Map<string, UnifiedDeliveryData>();

// Helper to generate cache keys
const getCacheKey = (combination: ZoneCombination): string => {
  return `${combination.originLeadTimeZoneId}-${combination.originFeeZoneId}-${combination.destinationLeadTimeZoneId}-${combination.destinationFeeZoneId}`;
};

/**
 * Fetches both lead time and delivery methods data in a single request
 */
export const getUnifiedDeliveryData = async (
  combination: ZoneCombination
): Promise<UnifiedDeliveryData> => {
  try {
    // Validate inputs
    if (
      !combination.originLeadTimeZoneId ||
      !combination.originFeeZoneId ||
      !combination.destinationLeadTimeZoneId ||
      !combination.destinationFeeZoneId
    ) {
      console.warn("Invalid zone combination:", combination);
      throw new Error(`Missing zone IDs: ${JSON.stringify(combination)}`);
    }

    const cacheKey = getCacheKey(combination);
    if (deliveryDataCache.has(cacheKey)) {
      return Promise.resolve(deliveryDataCache.get(cacheKey)!);
    }

    // Call unified endpoint if available
    try {
      const response = await query(`delivery-unified/calculate`, {
        method: "POST",
        body: JSON.stringify(combination),
      });

      if (response) {
        deliveryDataCache.set(cacheKey, response);
        return response;
      }
    } catch (e) {
      console.log(
        "Unified endpoint not available, falling back to separate calls"
      );
    }

    // Fall back to separate calls
    const [opsLeadTime, deliveryMethods] = await Promise.all([
      query(
        `delivery-lead-time/get-ops-lead-time?originZoneId=${combination.originLeadTimeZoneId}&destinationZoneId=${combination.destinationLeadTimeZoneId}`
      ),
      query(
        `delivery-methods/available?originZoneId=${combination.originFeeZoneId}&destinationZoneId=${combination.destinationFeeZoneId}`
      ),
    ]);

    const result: UnifiedDeliveryData = {
      ...combination,
      opsLeadTime: opsLeadTime || {
        opsLeadTime: 1,
        originZone: null,
        destinationZone: null,
      },
      deliveryMethods: deliveryMethods || { standard: true },
    };

    deliveryDataCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error("Error fetching unified delivery data:", error);

    // Return a default object to prevent app crashes
    return {
      ...combination,
      opsLeadTime: {
        opsLeadTime: 1,
        originZone: null,
        destinationZone: null,
      },
      deliveryMethods: { standard: true },
    };
  }
};

/**
 * Batch version for multiple products
 */
export const getBatchUnifiedDeliveryData = async (
  combinations: ZoneCombination[]
): Promise<UnifiedDeliveryData[]> => {
  // First, filter out combinations we already have in cache
  const uncachedCombinations = combinations.filter(
    (combination) => !deliveryDataCache.has(getCacheKey(combination))
  );

  if (uncachedCombinations.length > 0) {
    try {
      // Call batch unified endpoint if available
      const response = await query(`delivery-unified/batch-calculate`, {
        method: "POST",
        body: JSON.stringify({ combinations: uncachedCombinations }),
      });

      // Cache the results
      if (Array.isArray(response)) {
        response.forEach((result) => {
          const cacheKey = getCacheKey({
            originLeadTimeZoneId: result.originLeadTimeZoneId,
            originFeeZoneId: result.originFeeZoneId,
            destinationLeadTimeZoneId: result.destinationLeadTimeZoneId,
            destinationFeeZoneId: result.destinationFeeZoneId,
          });
          deliveryDataCache.set(cacheKey, result);
        });
      }
    } catch (error) {
      console.error(
        "Unified batch endpoint failed, falling back to individual calls"
      );
      // We'll fall back to individual calls below
    }
  }

  // Return results for all combinations (from cache or newly fetched)
  return Promise.all(
    combinations.map(async (combination) => {
      const cacheKey = getCacheKey(combination);

      // If we have it in cache, use that
      if (deliveryDataCache.has(cacheKey)) {
        return deliveryDataCache.get(cacheKey)!;
      }

      // Otherwise fetch it individually
      return getUnifiedDeliveryData(combination);
    })
  );
};
